import React from 'react';
import { Link } from 'react-router-dom';

const effectiveDate = 'January 2026';

const Wrapper = ({ title, testid, children }) => (
    <div className="container-page pt-32 pb-24 max-w-4xl" data-testid={testid}>
        <div className="label-caps text-brand-primary mb-6">/ Legal</div>
        <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl">{title}</h1>
        <div className="mt-3 label-caps text-white/40">Effective: {effectiveDate}</div>

        <article className="mt-10 space-y-8 text-white/80 leading-relaxed">
            {children}
        </article>

        <div className="mt-16 flex flex-wrap gap-3">
            <Link to="/contact" className="bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-6 py-3 label-caps transition-colors">
                Questions? Contact us
            </Link>
            <a href="tel:+19842010767" className="border border-white/20 text-white hover:bg-white/10 px-6 py-3 label-caps transition-colors">
                Call (984) 201-0767
            </a>
        </div>

        <div className="mt-12 flex flex-wrap gap-4 text-sm text-white/50 border-t border-white/10 pt-6">
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/cookies" className="hover:text-white transition-colors">Cookie Policy</Link>
            <Link to="/disclaimer" className="hover:text-white transition-colors">Disclaimer</Link>
        </div>
    </div>
);

const H2 = ({ children }) => (
    <h2 className="font-display font-black uppercase tracking-tight text-xl md:text-2xl text-white mt-8">{children}</h2>
);
const P = ({ children }) => <p className="text-white/80">{children}</p>;
const List = ({ items }) => (
    <ul className="list-disc list-outside pl-6 space-y-2 text-white/80">
        {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
);
const Address = () => (
    <div className="text-white/70 text-sm">
        <div><strong className="text-white">Dry Ice Bulls Cleaning System® LLC</strong></div>
        <div>Phone: <a href="tel:+19842010767" className="text-brand-primary hover:underline">(984) 201-0767</a></div>
        <div>Email: <a href="mailto:nc@dryicebulls.com" className="text-brand-primary hover:underline">nc@dryicebulls.com</a></div>
        <div>North Carolina, USA</div>
    </div>
);

// ============================================================================
// TERMS OF SERVICE
// ============================================================================
export const Terms = () => (
    <Wrapper title="Terms of Service" testid="terms-page">
        <P>
            These Terms of Service (the "Terms") govern your access to and use of the website located at
            dryicebulls.com (the "Site"), the equipment, services, training, and consulting offered by
            <strong> Dry Ice Bulls Cleaning System® LLC</strong> ("we", "us", "our"). By using the Site or ordering any
            service or equipment, you agree to these Terms.
        </P>

        <H2>1. Business-to-business only</H2>
        <P>
            Dry Ice Bulls Cleaning System® provides industrial and commercial cleaning services and sells or rents
            professional-grade cleaning equipment. Our services and equipment are intended for
            <strong> business, industrial, and commercial use only</strong>. We do not sell or provide
            services to private homes, individual consumers, or for personal / household use.
        </P>

        <H2>2. Services offered</H2>
        <List items={[
            'Mobile dry ice blasting, sandblasting, industrial pressure washing and laser cleaning services (in our service area).',
            'Sale and rental of dry ice cleaning machines, sandblasting equipment, accessories and nozzle kits.',
            'Training and consulting for industrial cleaning operators.',
            'Referral to third-party financing partners for qualifying equipment purchases.',
        ]} />

        <H2>3. Quotes, pricing and orders</H2>
        <P>
            Prices shown on the Site are indicative and may be updated without notice. A quote becomes a
            binding order only after we accept it in writing (email confirmation is sufficient) and, where
            applicable, receive the agreed deposit or financing approval. Machines shown with
            "Contact for Pricing" require a written quote before purchase.
        </P>

        <H2>4. Financing</H2>
        <P>
            Financing is offered through our third-party partner via
            {' '}<a className="text-brand-primary hover:underline" href="https://secure.financ.ing/app?dealercode=VZFJY&apptype=B&qs=apply" target="_blank" rel="noreferrer">secure.financ.ing</a>{' '}
            (dealer code VZFJY). Approval, terms, interest rate and monthly payments are set by the
            financing provider, not by Dry Ice Bulls Cleaning System®. We are not a lender and do not guarantee approval.
        </P>

        <H2>5. Delivery, installation and training</H2>
        <P>
            Delivery timelines are estimates. Title and risk of loss for equipment transfer to the buyer
            upon shipment from our facility unless otherwise agreed in writing. Training, when included,
            covers safe operation and basic maintenance; ongoing maintenance is the customer's
            responsibility.
        </P>

        <H2>6. Warranty and returns</H2>
        <P>
            New equipment carries the manufacturer's warranty applicable to that model. Used equipment is
            sold as-is unless a written warranty is provided. Consumables, nozzles and wear parts are not
            covered by warranty. Custom orders and financed equipment are non-refundable once shipped
            except as required by law.
        </P>

        <H2>7. Customer responsibilities</H2>
        <List items={[
            'Provide safe site access, power, water, and any required permits.',
            'Ensure operators are trained and use appropriate personal protective equipment (PPE).',
            'Comply with all applicable OSHA, EPA and local safety regulations.',
            'Notify us in writing of any known hazards on site before work begins.',
        ]} />

        <H2>8. Limitation of liability</H2>
        <P>
            To the maximum extent permitted by law, Dry Ice Bulls Cleaning System® is not liable for indirect,
            incidental, consequential, special or punitive damages, including lost profits, loss of use
            or downtime. Our total aggregate liability for any claim arising out of a service or order
            shall not exceed the amount actually paid by the customer for that specific service or
            product.
        </P>

        <H2>9. Intellectual property</H2>
        <P>
            "Dry Ice Bulls Cleaning System®" and the Dry Ice Bulls logo are registered trademarks of Dry Ice Bulls Cleaning System® LLC.
            All content on this Site (text, images, graphics, videos) is owned by Dry Ice Bulls Cleaning System® or its
            licensors and may not be copied, reproduced or redistributed without written permission.
        </P>

        <H2>10. Governing law</H2>
        <P>
            These Terms are governed by the laws of the State of North Carolina, USA, without regard to
            its conflict of law rules. Any dispute shall be resolved in the state or federal courts
            located in North Carolina, and each party consents to that exclusive jurisdiction.
        </P>

        <H2>11. Changes</H2>
        <P>
            We may update these Terms from time to time. The updated version takes effect when posted on
            the Site. Continued use of the Site or our services after changes are posted constitutes
            acceptance of the revised Terms.
        </P>

        <H2>12. Contact</H2>
        <Address />
    </Wrapper>
);

// ============================================================================
// PRIVACY POLICY
// ============================================================================
export const Privacy = () => (
    <Wrapper title="Privacy Policy" testid="privacy-page">
        <P>
            Dry Ice Bulls Cleaning System® LLC ("we", "us", "our") respects your privacy. This policy explains what
            personal information we collect through dryicebulls.com (the "Site") and our business
            operations, how we use it, and your rights.
        </P>

        <H2>1. Information we collect</H2>
        <List items={[
            'Contact form data: name, email address, phone number, company name, subject, message and (optionally) the machine you are interested in.',
            'Communications: emails, WhatsApp, phone calls and messages you send us.',
            'Order data: billing / shipping address, purchase history and (where applicable) financing partner information.',
            'Technical data: IP address, browser type, device type, pages viewed and referral source, collected via cookies and standard server logs.',
        ]} />

        <H2>2. How we use your information</H2>
        <List items={[
            'To respond to quote requests, service inquiries and support questions.',
            'To fulfil orders, deliver equipment, schedule on-site services and provide training.',
            'To share information with our financing partner when you request financing (never without your consent).',
            'To improve the Site, prevent fraud and comply with legal obligations.',
            'To send occasional operational or marketing communications — you can opt out at any time.',
        ]} />

        <H2>3. Legal bases (EU / UK visitors)</H2>
        <P>
            Where GDPR applies, we process personal data on the basis of your consent (marketing),
            performance of a contract (quotes and orders), our legitimate business interests (site
            improvement, fraud prevention) or legal obligation.
        </P>

        <H2>4. Sharing</H2>
        <P>
            We do not sell your personal information. We share it only with:
        </P>
        <List items={[
            'Our financing partner (secure.financ.ing) if and only if you request a financing application.',
            'Trusted service providers who host our website, process payments, ship equipment or send email on our behalf, under confidentiality obligations.',
            'Law enforcement or regulators when required by law.',
        ]} />

        <H2>5. Data retention</H2>
        <P>
            We keep contact and order records for as long as needed to provide our services, comply with
            tax and accounting rules, and enforce our agreements — typically up to 7 years for order
            data, and up to 3 years for general contact enquiries. You may request deletion at any time
            (see section 7).
        </P>

        <H2>6. Security</H2>
        <P>
            We use commercially reasonable safeguards to protect your data: encrypted connections
            (HTTPS), hashed admin passwords, access controls and secure hosting. No online service is
            100% secure — please avoid sending sensitive financial details by email.
        </P>

        <H2>7. Your rights</H2>
        <List items={[
            'Access — request a copy of the personal data we hold about you.',
            'Correction — ask us to fix inaccurate information.',
            'Deletion — ask us to erase your personal data, subject to legal retention requirements.',
            'Opt-out — unsubscribe from marketing communications at any time.',
            'For California residents (CCPA/CPRA) and EU/UK residents (GDPR), additional rights apply.',
        ]} />

        <H2>8. Children</H2>
        <P>
            Our services are B2B and are not directed to children under 16. We do not knowingly collect
            personal data from children.
        </P>

        <H2>9. International visitors</H2>
        <P>
            Our servers and business operations are located in the United States. If you access the Site
            from outside the U.S., your data will be transferred to and processed in the U.S.
        </P>

        <H2>10. Changes</H2>
        <P>
            We may update this policy. The current version is always posted on this page with its
            effective date.
        </P>

        <H2>11. Contact</H2>
        <P>To exercise any of your rights or ask a question about this policy, contact:</P>
        <Address />
    </Wrapper>
);

// ============================================================================
// COOKIE POLICY
// ============================================================================
export const Cookies = () => (
    <Wrapper title="Cookie Policy" testid="cookies-page">
        <P>
            This Cookie Policy explains how Dry Ice Bulls Cleaning System® LLC uses cookies and similar technologies on
            dryicebulls.com (the "Site").
        </P>

        <H2>1. What are cookies?</H2>
        <P>
            Cookies are small text files placed on your device by websites you visit. They are widely
            used to make websites work, or work more efficiently, and to provide reporting information.
        </P>

        <H2>2. Categories of cookies we use</H2>
        <List items={[
            'Strictly necessary cookies — required for the Site to function (e.g. the admin session cookie that keeps our team logged in to the content dashboard). These cannot be switched off.',
            'Functional cookies — remember choices you make, such as menu preferences.',
            'Analytics cookies — help us understand how the Site is used so we can improve it. We only enable these with your consent where required by law.',
            'Third-party cookies — set by embedded content, e.g. YouTube video players in our Videos section, subject to those providers\' own policies.',
        ]} />

        <H2>3. Specific cookies used by this Site</H2>
        <List items={[
            'access_token — HTTP-only session cookie used only by administrators when logged into the CMS. Not set for regular visitors.',
            'dib_admin_token (localStorage) — used only by administrators for admin authentication.',
            'YouTube / Vimeo cookies — set only if you play an embedded video, controlled by YouTube / Vimeo.',
        ]} />

        <H2>4. Managing cookies</H2>
        <P>
            You can control and delete cookies via your browser settings. Blocking strictly-necessary
            cookies may make some parts of the Site unusable. To disable third-party video cookies,
            simply don't play the embedded videos.
        </P>

        <H2>5. Do Not Track</H2>
        <P>
            We honour the "Global Privacy Control" (GPC) signal where required by law. Our Site does not
            currently use targeted advertising cookies.
        </P>

        <H2>6. Changes</H2>
        <P>
            We may update this policy as our cookie usage evolves. The current version is always posted
            here with its effective date.
        </P>

        <H2>7. Contact</H2>
        <Address />
    </Wrapper>
);

// ============================================================================
// DISCLAIMER
// ============================================================================
export const Disclaimer = () => (
    <Wrapper title="Disclaimer" testid="disclaimer-page">
        <H2>1. Informational purpose</H2>
        <P>
            The content on dryicebulls.com — including service descriptions, machine specifications,
            videos, news articles and pricing indications — is provided for general information only.
            While we work hard to keep it accurate and current, we make no warranties of any kind,
            express or implied, about the completeness, accuracy, reliability or availability of the
            information on the Site.
        </P>

        <H2>2. No professional advice</H2>
        <P>
            Any technical guidance, application notes, videos or recommendations on the Site are general
            in nature and should not be treated as a substitute for a formal on-site assessment by a
            qualified Dry Ice Bulls Cleaning System® technician. Actual cleaning results depend on substrate, contamination
            type, environmental conditions and operator skill.
        </P>

        <H2>3. Industrial and commercial use only</H2>
        <P>
            Dry ice blasting, sandblasting, industrial pressure washing and laser cleaning are
            industrial processes involving high pressure, cryogenic media, abrasives and/or Class 4
            lasers. Our equipment and services are strictly for trained industrial and commercial
            operators. They are not intended for consumer or residential use. Improper operation can
            cause serious injury, property damage or death.
        </P>

        <H2>4. Financing disclaimer</H2>
        <P>
            Financing is offered through an independent third-party partner (secure.financ.ing, dealer
            code VZFJY). Dry Ice Bulls Cleaning System® is not a lender and does not underwrite, approve or set the
            terms of any financing agreement. All financing terms, interest rates and approvals are
            determined solely by the financing partner.
        </P>

        <H2>5. External links</H2>
        <P>
            The Site may link to external websites, including
            {' '}<a className="text-brand-primary hover:underline" href="https://dryicemarketplace.com" target="_blank" rel="noreferrer">dryicemarketplace.com</a>{' '}
            and third-party video platforms. We do not control and are not responsible for the content
            or practices of those external sites.
        </P>

        <H2>6. Trademarks</H2>
        <P>
            "Dry Ice Bulls Cleaning System®" and the Dry Ice Bulls Cleaning System® Cleaning System logo are registered trademarks of
            Dry Ice Bulls Cleaning System® LLC. Other trademarks and product names mentioned on the Site are the
            property of their respective owners and are used for identification purposes only.
        </P>

        <H2>7. Limitation of liability</H2>
        <P>
            To the fullest extent permitted by law, Dry Ice Bulls Cleaning System® shall not be liable for any loss or
            damage — direct, indirect, incidental or consequential — arising from your reliance on any
            information provided on the Site. Refer to our Terms of Service for the complete liability
            terms that apply to services and equipment orders.
        </P>

        <H2>8. Contact</H2>
        <Address />
    </Wrapper>
);

export default Privacy;
